import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { Effect, Actions, toPayload } from '@ngrx/effects';
import { switchMap, catchError, map, tap, delay } from 'rxjs/operators';
import { AuthData } from "../../providers/auth";
import { App } from "ionic-angular";
import { IUser, IUserInfo } from '../../models/user.model';
import { StorageFb } from '../../providers/storage';
import * as authActions from '../actions/auth.actions';
import * as paymentActions from '../actions/payment.actions';
import * as fromAuth from '../reducers/auth.reducer';
import { UtilitiesProvider } from '../../providers/utilities';
import { ENV } from '@app/env';
import { TrackingData } from '../../providers/tracking';
import { TranslateCodePipe } from '../../pipes/translate';
import { has } from 'lodash';
import { AnalyticsData, _ACTIONS } from '../../providers/analytics';

@Injectable()
export class AuthEffects {

  @Effect()
  verifyAuth$ = this.actions
    .ofType(authActions.VERIFY_AUTH)
    .switchMap(() => this.authProvider.getLocalUser())
    .pipe(
      switchMap((user) => this.authProvider.getUserDB(user.uid, this.clientId)
        .pipe(
          map((dbUser: any) => {
            if (dbUser) {
              return new authActions.Authenticated({ info: dbUser });
            } else {
              return new authActions.NotAuthenticated();
            }
          }),
          catchError(err => {
            return Observable.of(new authActions.NotAuthenticated(err));
          })
        )),
      catchError(err => {
        return Observable.of(new authActions.NotAuthenticated(err));
      }),
    )

  @Effect({ dispatch: false })
  authenticated$ = this.actions
    .ofType(authActions.AUTHENTICATED)
    .map(toPayload)
    .pipe(
      tap((dbUser: IUser) => {
        this.utilities.consoleDecorator('IUser authenticated$ detected interest', dbUser);
        if (dbUser.info) {
          if (!dbUser.info.interest || dbUser.info.interest === {}) {
            this.appCtrl.getActiveNav().setRoot('interest', {}, { animate: true, direction: 'forward' });
          }

          if (this.appCtrl.getActiveNav()._views.length === 0) {
            this.appCtrl.getActiveNav().setRoot('room', {}, { animate: true, direction: 'forward' });
          }

          this.store.dispatch(new paymentActions.SetDefaultCard(dbUser.info.defaultCard || null));

          this.trackingAuth(_ACTIONS.currentlyAuthenticated, { userInfo: dbUser.info, userId: dbUser.info.id, provider: null, configAuth: null });
        } else {
          this.utilities.consoleError('authenticated$ - User not found in database', dbUser);
        }
      }),
      delay(2000),
      tap(() => this.changeSplashStatus(false)),
    );

  @Effect({ dispatch: false })
  notAuthenticated$ = this.actions
    .ofType(authActions.NOT_AUTHENTICATED)
    .pipe(
      tap(() => {
        const currentView = this.appCtrl.getActiveNav().getActive();
        this.utilities.consoleDecorator('Existe una vista actual', [currentView]);
        if (currentView) {
          this.utilities.consoleDecorator('Validando pagina actual esta en lista blanca', [this.whiteComponentList, currentView.component.name, !this.whiteComponentList.find(k => k === currentView.id.split("/")[0])]);
          if (!this.whiteComponentList.find(k => k === currentView.id.split("/")[0])) {
            this.appCtrl.getActiveNav().setRoot('auth', {}, { animate: true, direction: 'forward' });
          }
        } else {
          this.appCtrl.getActiveNav().setRoot('auth', {}, { animate: true, direction: 'forward' });
        }
      }),
      tap(() => this.changeSplashStatus(false)),
    );

  @Effect()
  signUp$: Observable<Action> = this.actions
    .ofType(authActions.SIGN_UP)
    .map(toPayload)
    .switchMap((payload: any) =>
      this.authProvider.loginRegisterCustom(payload, payload.clientAuth)
        .pipe(
          switchMap((userAuth: { customToken: string, code: string, userId: string, message: string, info: any }) =>
            Observable.fromPromise(this.authProvider.loginCustomUser(userAuth.customToken))
              .do(() => this.trackingAuth(_ACTIONS.register, { userInfo: userAuth.info, userId: userAuth.userId, provider: "email", configAuth: payload.clientAuth }))
              .mergeMap(() => {
                return [new authActions.SignUpSuccess({ info: userAuth.info }), new paymentActions.SetDefaultCard(null)];
              })
              .catch(err => [new authActions.SignUpFailed(err)])
          ),
          catchError(err => [new authActions.SignUpFailed(err)]),
        ));

  @Effect({ dispatch: false })
  signUpSuccess$ = this.actions
    .ofType(authActions.SIGN_UP_SUCCESS)
    .map(toPayload)
    .pipe(
      tap(() => this.authProvider.plusClientUserRegisteredStat()),
      tap((dbUser: IUser) => {
        const load = this.utilities.setLoading();
        const currentView = this.appCtrl.getActiveNav().getActive();
        if (has(currentView.data, 'isModal')) {
          if (currentView.data.isModal) {
            load.dismiss();
            return true;
          }
        }

        if (dbUser.info) {
          if (!dbUser.info.interest || Object.keys(dbUser.info.interest).length === 0) {
            return this.appCtrl.getActiveNav().setRoot('interest', {}, { animate: true, direction: 'forward' });
          }
          if (this.appCtrl.getActiveNav()._views.length === 0) {
            return this.appCtrl.getActiveNav().setRoot('room', {}, { animate: true, direction: 'forward' });
          }
          if (currentView.component.name === 'UserRegisterPage') {
            return this.appCtrl.getActiveNav().setRoot('room', {}, { animate: true, direction: 'forward' });
          }
        } else {
          // Si el usuario no esta creado en la base de datos del cliente se debe enviar la creacion en el child
          this.utilities.consoleError('authenticated$ - User not found in database', dbUser);
        }
      })
    )

  @Effect({ dispatch: false })
  signUpFailed$ = this.actions
    .ofType(authActions.SIGN_UP_FAILED)
    .map(toPayload)
    .do(payload => this.utilities.consoleDecorator('Payload: ', payload))
    .do(payload => {
      let message = "Error interno";
      if (has(payload, 'error')) {
        message = payload.error.message;
      } else if (has(payload, 'message')) {
        message = payload.message;
      }

      this.trackingAuth(_ACTIONS.registerFailed, payload.error);

      return this.utilities.presentAlert('Error', message);
    });

  @Effect()
  loginSocialCustom$: Observable<Action> = this.actions
    .ofType(authActions.LOGIN_SOCIAL_CUSTOM)
    .map(toPayload)
    .switchMap((payload: any) =>
      this.authProvider.loginSocialCustom(payload, payload.clientAuth)
        .pipe(
          switchMap((userAuth: { customToken: string, code: string, userId: string, message: string, info: any, existingUser: boolean }) => {
            return Observable.fromPromise(this.authProvider.loginCustomUser(userAuth.customToken))
              .switchMap((resultLogin) => {
                this.utilities.consoleDecorator('User social login custom result', [resultLogin, userAuth]);
                return Observable.of(new authActions.LoginSuccess({ info: userAuth.info }));
              })
              .do(() => {
                const action = userAuth.existingUser ? _ACTIONS.login : _ACTIONS.register;
                this.trackingAuth(action, { userInfo: userAuth.info, userId: userAuth.userId, provider: payload.provider, configAuth: payload.clientAuth });
              })
              .catch(err => [new authActions.LoginFailed(err)])
          }),
          catchError(err => [new authActions.LoginFailed(err)]),
        ));

  @Effect()
  loginEmailCustom$: Observable<Action> = this.actions
    .ofType(authActions.LOGIN_EMAIL_CUSTOM)
    .map(toPayload)
    .switchMap((payload: { email: string, password: string, clientAuth: any }) =>
      this.authProvider.loginEmailCustom(payload, payload.clientAuth)
        .pipe(
          switchMap((userAuth: { customToken: string, code: number, userId: string, message: string, info: any }) => {
            if (userAuth.code === 206) {
              return Observable.of(new authActions.LoginRestorePassword({ clientAuth: payload.clientAuth }));
            } else {
              return Observable.fromPromise(this.authProvider.loginCustomUser(userAuth.customToken))
                .switchMap((resultLogin) => {
                  this.utilities.consoleDecorator('User email login custom result', [resultLogin, userAuth]);
                  return Observable.of(new authActions.LoginSuccess({ info: userAuth.info }));
                })
                .do(() => this.trackingAuth(_ACTIONS.login, { userInfo: userAuth.info, userId: userAuth.userId, provider: "email", configAuth: payload.clientAuth }))
                .catch(err => [new authActions.LoginFailed(err)])
            }
          }),
          catchError(err => [new authActions.LoginFailed(err)]),
        ));

  @Effect()
  loginTokenCustom$: Observable<Action> = this.actions
    .ofType(authActions.LOGIN_TOKEN_CUSTOM)
    .map(toPayload)
    .switchMap((payload: { token: string, info: any }) =>
      Observable.fromPromise(this.authProvider.loginCustomUser(payload.token))
        .switchMap(() => Observable.of(new authActions.LoginSuccess({ info: payload.info })))
        .catch(err => [new authActions.LoginFailed(err)])
    )

  @Effect()
  loginSuccess$ = this.actions
    .ofType(authActions.LOGIN_SUCCESS)
    .map(toPayload)
    .do((user: IUser) => {
      const load = this.utilities.setLoading();
      const currentView = this.appCtrl.getActiveNav().getActive();
      if (has(currentView.data, 'isModal')) {
        if (currentView.data.isModal) {
          load.dismiss();
          return true;
        }
      }

      this.utilities.consoleDecorator('Validate user auth interest', user);
      if (!user.info.interest || Object.keys(user.info.interest).length === 0) {
        return this.appCtrl.getActiveNav().setRoot('interest', {}, { animate: true, direction: 'forward' });
      } else {
        return this.appCtrl.getActiveNav().setRoot('room', {}, { animate: true, direction: 'forward' });
      }
    })
    .map((user: IUser) => new paymentActions.SetDefaultCard(user.info.defaultCard || null));

  @Effect({ dispatch: false })
  loginFailed$ = this.actions
    .ofType(authActions.LOGIN_FAILED)
    .map(toPayload)
    .do(payload => this.utilities.consoleDecorator('Fail login: ', payload))
    .do(payload => {
      let message = "Error interno";
      if (has(payload, 'error')) {
        message = payload.error.info;
      } else if (has(payload, 'message')) {
        message = payload.message;
      }

      this.trackingAuth(_ACTIONS.loginFailed, payload.error);

      return this.utilities.presentAlert('Error', message);
    });

  @Effect()
  loginRestorePassword$ = this.actions
    .ofType(authActions.LOGIN_RESTORE_PASSWORD)
    .map(toPayload)
    .switchMap((payload) => Observable.fromPromise(this.presentAlertRestorePass(false))
      .switchMap(email => this.authProvider.resetPassword(email, payload.clientAuth)
        .map(() => this.utilities.setToastStatic('Se ha enviado un correo electrónico con las instrucciones para restaurar la contraseña', 'Ok', 'top'))
        .map(() => new authActions.NotAuthenticated())
        .catch(err => {
          this.utilities.setToastStatic(err.error.message, 'Ok', 'button');
          return [new authActions.NotAuthenticated(err)];
        })
      )
      .catch(err => [new authActions.NotAuthenticated(err)])
    )


  @Effect()
  logout$: Observable<Action> = this.actions
    .ofType(authActions.LOGOUT)
    .switchMap(() => this.authProvider.logoutUser())
    .switchMap(() => [new authActions.NotAuthenticated(), new paymentActions.SetInitialPay()])
    .catch(err => [new authActions.NotAuthenticated(err), new paymentActions.SetInitialPay()]);

  @Effect()
  updateProfile$: Observable<Action> = this.actions
    .ofType(authActions.UPDATE_PROFILE)
    .map(toPayload)
    .withLatestFrom(this.store.select(fromAuth.getUser))
    .switchMap(([payload, user]) => {
      return Observable.fromPromise(this.authProvider.updateUser(payload, this.clientId, user.info.id))
        .switchMap(() => this.trackingProvider.trackEntity('Users', user.info.id, this.clientId, payload, 'update'))
        .switchMap(() =>
          this.authProvider.getUserDB(user.info.id, this.clientId)
            .map((user: IUserInfo) => new authActions.UpdateProfileSuccess({ info: user }))
            .catch(err => [new authActions.UpdateProfileFailed(err)])
        )
        .catch(err => [new authActions.UpdateProfileFailed(err)])
    });

  @Effect({ dispatch: false })
  updateProfileSuccess$ = this.actions
    .ofType(authActions.UPDATE_PROFILE_SUCCESS)
    .do(() => this.utilities.presentToast('Update profile success'));

  @Effect()
  changeAvatar$: Observable<Action> = this.actions
    .ofType(authActions.CHANGE_AVATAR)
    .map(toPayload)
    .withLatestFrom(this.store.select(fromAuth.getUser))
    .switchMap(([payload, user]: [File, IUser]) =>
      Observable.fromPromise(this.storageProvider.uploadAvatar(payload, user.info.id))
        .switchMap(result => {
          return Observable.fromPromise(this.authProvider.updateUser({ photos: [result.downloadURL], defaultPhoto: 0 }, this.clientId, user.info.id))
            .switchMap(() =>
              this.authProvider.getUserDB(user.info.id, this.clientId)
                .map((user: IUserInfo) => new authActions.UpdateProfileSuccess({ info: user }))
                .catch(err => [new authActions.UpdateProfileFailed(err)])
            )
            .catch(err => [new authActions.UpdateProfileFailed(err)]);
        })
        .catch(err => [new authActions.UpdateProfileFailed(err)])
    );

  private whiteComponentList = [
    // Se debe poner la parte inicial de la url
    "tickets",
    "ticket",
    "restore",
  ];

  private clientId = ENV.client;

  constructor(
    private actions: Actions,
    private storageProvider: StorageFb,
    private authProvider: AuthData,
    private store: Store<any>,
    private appCtrl: App,
    private utilities: UtilitiesProvider,
    private trackingProvider: TrackingData,
    private analyticsProvider: AnalyticsData,
    private translatePipe: TranslateCodePipe,
  ) { }

  private changeSplashStatus(enable: boolean) {
    document.getElementById('splashContainer').style.display = enable ? 'block' : 'none';
  }

  private presentAlertRestorePass(requiredClient: boolean = true): Promise<any> {
    return new Promise(async (res, rej) => {
      this.utilities.alert.create({
        title: await this.translatePipe.transform('Proceso de restauración de contraseña'),
        message: await this.translatePipe.transform(
          requiredClient ?
            'Introduzca el correo electŕonico registrado para iniciar el proceso de recordar contraseña. El sistema enviará un correo electrónico con las instrucciones para restablecerla.' :
            'Por seguridad, para continuar es necesario actualizar la contraseña, introduzca el correo electronico registrado y el sistema enviará un correo electrónico con las instrucciones para restablecer su contraseña.'
        ),
        enableBackdropDismiss: false,
        inputs: [{
          type: 'email',
          placeholder: await this.translatePipe.transform('Email registrado'),
          name: 'email',
        }],
        buttons: [{
          text: await this.translatePipe.transform('Cancel'),
          role: 'cancel',
          handler: () => {
            rej();
          }
        }, {
          text: await this.translatePipe.transform('Continue'),
          handler: (result) => {
            if (result['email']) {
              res(result['email'])
            } else {
              return false;
            }
          }
        }]
      }).present();
    });
  }

  private trackingAuth(action, payload) {
    return this.analyticsProvider
      .savePageTrackOnFirestore(
        "AuthPage",
        action,
        window.localStorage.getItem(`${ENV.client}-PROCESS-LOAD`),
        payload,
      );
  }
}



// WEBPACK FOOTER //
// ./src/ngrx/effects/auth.effects.ts